import { formatDate, parseISO } from "date-fns"
import { Badge } from "design-system/atoms/Badge"
import { cn } from "design-system/utils"
import { CircleSlash2, MessageCircleQuestion, Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"
import { SupportConversationQuestionResponseDataSchema } from "schema"
import z from "zod"

type Props = {
  question: z.infer<typeof SupportConversationQuestionResponseDataSchema>
  highlight?: boolean
}

export function QuestionContent({ question, highlight }: Props) {
  const { t } = useTranslation()

  const isResolvedAutomatically = question.resolvedAutomaticallyAt
  const BadgeIcon = isResolvedAutomatically ? Sparkles : CircleSlash2
  const badgeCopy = isResolvedAutomatically
    ? t("support.timeline.resolvedAutomatically")
    : t("support.timeline.notResolved")

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="w-full flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-1 items-center text-support dark:text-support-secondary">
          <MessageCircleQuestion size={16} />
          <span className="text-sm font-medium">{t("support.timeline.question")}</span>
        </div>
        <div className="flex flex-row gap-4 items-center">
          <Badge variant="secondary">
            <BadgeIcon size={16} />
            <span>{badgeCopy}</span>
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatDate(parseISO(question.createdAt), "Pp")}
          </span>
        </div>
      </div>
      <div
        className={cn(
          "overflow-hidden text-left text-sm border border-border rounded-lg p-4",
          highlight && "border-warning",
        )}
      >
        {question.summary}
      </div>
    </div>
  )
}
